/**
 * What the wall panel's Backsplash card reads and writes (contractor call
 * item #9). Pure.
 *
 * A kitchen backsplash is DERIVED from the cabinets and redrawn by every
 * writer, so the card never edits a band: it edits the wall's setting
 * (`room.walls[i].backsplashSpec`, read by the one band builder — see
 * `@shared-lib/kitchen-assembly/styling/backsplashSpec`) and redraws the
 * bands from it in the SAME write, so the screen and the stored room cannot
 * disagree. Every later writer — a cabinet move, a tile pick, a package, a
 * new layout — reads the same setting and draws the same bands.
 */
import {
  buildBacksplashRegions,
  findBacksplashRegions,
  isGeneratedKitchenBacksplash,
  joinRoomOpenings,
  rebuildGeneratedBacksplash,
  retextureBacksplashWalls,
  stripGeneratedBacksplash,
  unhydratedOpenings,
  wallCounterFacts,
  wallIdsFor,
  isCountertop,
  isLowerCab,
  isUpperCab,
  isStove,
  isHood,
  isMicrowave,
  standsThroughBand,
} from '@shared-lib/kitchen-assembly/styling/kitchenBacksplash';
import { readBacksplashSpec } from '@shared-lib/kitchen-assembly/styling/backsplashSpec';
import { resolveRoomKind } from '@shared-lib/rooms/roomKind';
import { planDefaultBacksplash } from '@shared-lib/kitchen-assembly/styling/defaultKitchenBacksplash';
import {
  DEFAULT_BACKSPLASH_CONFIG,
  isUnchosenBacksplash,
} from '@shared-lib/kitchen-assembly/styling/unchosenBacksplash';
import { projectExtentOntoWall } from '@shared-lib/kitchen-assembly/styling/backsplashRegion';
import { wallFaceSpanY, wallFaceHeight } from '@shared-lib/geometry/wallFace';
import { wallBathroomBands } from './bathroomBandCard';

const SQ_M_PER_SQ_FT = 0.09290304;

// The DRAWN face (`wallFace`): a band's percentages are of it.
const wallHeightOf = (wall) => wallFaceHeight(wall) ?? 0;
const wallBottomOf = (wall) =>
  wallFaceSpanY(wall)?.bottom ?? wall?.center?.y ?? 0;
const topOf = (o) => (o.center?.y ?? 0) + (o.dimensions?.y ?? 0) / 2;
const bottomOf = (o) => (o.center?.y ?? 0) - (o.dimensions?.y ?? 0) / 2;

/** The generated bands on one wall. */
const generatedBandsOn = (wall) =>
  (wall?.texture?.regions || []).filter(isGeneratedKitchenBacksplash);

/**
 * What the card shows for one wall.
 *
 * @param {Object} room the room as stored; its window copies may be slim
 * @param {string} wallId
 * @param {{windows?: Array, openings?: Array}} [structureOpenings] the
 *   structure's opening copies, for the windows the room copies only name
 * @returns {{spec, off, bands, hasCounter, hasRange, hasHood,
 *   hasWindowOverCounter, sqFt}}
 */
export function wallBacksplash(room, wallId, structureOpenings) {
  const wall = (room?.walls || []).find((w) => w.id === wallId);
  if (!wall) return null;
  const spec = readBacksplashSpec(wall);
  const bands = generatedBandsOn(wall);
  const objById = new Map((room.objects || []).map((o) => [o.id, o]));
  const onWall = (o) => wallIdsFor(o, objById)[0] === wallId;
  // the builder's own reading of the wall's counter and the window over it
  const { hasCounter, sillY } = wallCounterFacts(
    joinRoomOpenings(room, structureOpenings),
    wallId
  );

  const len = wall.dimensions?.x ?? 0;
  const height = wallHeightOf(wall);
  const m2 = bands.reduce(
    (sum, b) => sum + (b.width / 100) * len * ((b.height / 100) * height),
    0
  );

  return {
    spec,
    off: spec.off,
    bands,
    hasCounter,
    hasRange: (room.objects || []).some((o) => isStove(o) && onWall(o)),
    hasHood: (room.objects || []).some((o) => isHood(o) && onWall(o)),
    hasWindowOverCounter: sillY != null,
    sqFt: m2 / SQ_M_PER_SQ_FT,
  };
}

/**
 * Which room wall the Backsplash card is for, given the wall the panel was
 * opened on — or null when there is no card. `kind` says which card: a
 * kitchen wall's (`BacksplashCard`) or a bathroom wall's
 * (`BathroomBandsCard` — a bathroom's wall with a shower, a vanity or a
 * tub, or a band of one; in any other room, a wall with a shower or a tub).
 *
 * Read from the WALL, not from the selected room: first-person keeps no room
 * selected, and neither does the whole-house view, so a card that needed one
 * never showed there. A dollhouse click names the STRUCTURE wall, which can
 * back several room walls; the one that can carry a backsplash wins.
 *
 * @param {Iterable<object>} rooms every room, the preferred one first if given
 * @param {string} wallId a room wall's id, or a structure wall's
 * @param {?object} preferred the selected room, looked at first
 * @returns {?{room, wallId, kind: 'kitchen'|'bathroom'}}
 */
export function backsplashCardTarget(
  rooms,
  wallId,
  preferred,
  structureOpenings
) {
  if (!wallId) return null;
  const ordered = [preferred, ...(rooms || [])].filter(Boolean);
  for (const room of ordered) {
    const matches = (room.walls || []).filter(
      (w) => w.id === wallId || w.alignment?.structureWallId === wallId
    );
    if (!matches.length) continue;
    const kinds = resolveRoomKind(room);
    if (!kinds.has('kitchen')) {
      // A bathroom's walls; elsewhere only a wall with a shower or a tub —
      // a counter in a laundry or a bar keeps Edit wall's own sections.
      const wall = matches.find((w) => {
        const facts = wallBathroomBands(room, w.id);
        return (
          facts && (kinds.has('bathroom') || !!facts.shower || !!facts.tub)
        );
      });
      if (wall) return { room, wallId: wall.id, kind: 'bathroom' };
      continue;
    }
    const carries = (w) => {
      const facts = wallBacksplash(room, w.id, structureOpenings);
      return (
        facts &&
        (facts.hasCounter ||
          facts.bands.length > 0 ||
          !!facts.spec.counter ||
          facts.off)
      );
    };
    const wall = matches.find(carries);
    if (wall) return { room, wallId: wall.id, kind: 'kitchen' };
  }
  return null;
}

/**
 * The change "Use on every wall in this kitchen" makes when it is turned
 * on: THIS wall's own choices, and only those — never a range setting from a
 * wall without a range, and never an empty setting, which would clear the
 * other walls' choices. Null when the wall has none of its own.
 */
export function copyHeightsChange(facts) {
  const change = {};
  if (facts?.spec?.counter) change.counter = facts.spec.counter;
  if (facts?.hasRange && facts?.spec?.range) change.range = facts.spec.range;
  return Object.keys(change).length ? change : null;
}

/**
 * The kitchen's tile per kind — the one most of that kind's bands wear (the
 * rebuild's rule) — and whether it is still the default nobody picked.
 *
 * @returns {{counter: ?{textureId, unchosen}, stove: ?{textureId, unchosen}}}
 */
export function kitchenTiles(walls) {
  const bands = (walls || []).flatMap(generatedBandsOn);
  const tileOf = (kind) => {
    const ofKind = bands.filter((b) => b.kind === kind && b.id);
    if (!ofKind.length) return null;
    const tally = new Map();
    for (const b of ofKind) tally.set(b.id, (tally.get(b.id) || 0) + 1);
    const [textureId] = [...tally].sort((a, b) => b[1] - a[1])[0];
    const wearing = ofKind.filter((b) => b.id === textureId);
    return { textureId, unchosen: wearing.every(isUnchosenBacksplash) };
  };
  return { counter: tileOf('counter'), stove: tileOf('stove') };
}

/** `spec` with `change` folded in; undefined when nothing is left set. */
function foldSpec(spec, change) {
  const out = { ...(spec || {}) };
  for (const band of ['counter', 'range']) {
    if (!(band in change)) continue;
    if (change[band]) out[band] = change[band];
    else delete out[band];
  }
  if ('off' in change) {
    if (change.off) out.off = true;
    else delete out.off;
  }
  return Object.keys(out).length ? out : undefined;
}

const withSpec = (wall, spec) => {
  const { backsplashSpec: _old, ...rest } = wall;
  return spec ? { ...rest, backsplashSpec: spec } : rest;
};

/**
 * The room's walls after a change the card makes, with the bands redrawn —
 * or why it cannot be made, in which case nothing changes.
 *
 * `change` is any of `{ counter, range, off }` (`null` clears a band's
 * setting). With `scope: 'kitchen'` the HEIGHTS (never `off`) go on every
 * backsplash wall of the kitchen — every wall wearing a generated band or a
 * setting — as "Use on every wall in this kitchen" asks.
 *
 * Redrawing: the kitchen's bands are rebuilt against the new settings, in
 * the tile each kind wears; when a wall is turned back on and the kitchen
 * has no band left, it gets the default tile, marked not chosen. A wall the
 * builder cannot see the windows of is refused, as every other writer
 * refuses it.
 *
 * @returns {{walls: ?Array, reason: ?string}} `reason` is null on success,
 *   else 'unhydrated-openings' | 'rebuild-declined' | a
 *   `planDefaultBacksplash` reason
 */
export function planBacksplashChange(
  room,
  { wallId, change, scope = 'wall' },
  { structureOpenings, generatedAt } = {}
) {
  const heights = {};
  for (const band of ['counter', 'range'])
    if (band in change) heights[band] = change[band];
  const reaches = (w) =>
    w.id === wallId ||
    (scope === 'kitchen' &&
      (generatedBandsOn(w).length > 0 || w.backsplashSpec));
  const walls = (room.walls || []).map((w) => {
    if (!reaches(w)) return w;
    const next = foldSpec(w.backsplashSpec, w.id === wallId ? change : heights);
    return withSpec(w, next);
  });
  const next = { ...room, walls };

  if (unhydratedOpenings(joinRoomOpenings(next, structureOpenings)).length) {
    // Taking a wall's band OFF puts no tile anywhere, so it needs no window:
    // that wall alone is stripped, the rest left as they are.
    const onlyRemoves =
      change.off === true && !('counter' in change) && !('range' in change);
    return onlyRemoves
      ? {
          walls: walls.map((w) =>
            w.id === wallId ? stripGeneratedBacksplash([w])[0] : w
          ),
          reason: null,
        }
      : { walls: null, reason: 'unhydrated-openings' };
  }
  const opts = { structureOpenings, generatedAt };
  const hasBands = findBacksplashRegions(walls).some(({ region }) =>
    isGeneratedKitchenBacksplash(region)
  );
  if (hasBands) {
    const rebuilt = rebuildGeneratedBacksplash(next, opts);
    if (rebuilt) return { walls: rebuilt, reason: null };
    // The rebuild draws nothing when every banded wall is now off — the
    // last band taken off, not a refusal.
    const anything = buildBacksplashRegions(
      joinRoomOpenings(next, structureOpenings),
      DEFAULT_BACKSPLASH_CONFIG,
      {}
    ).length;
    return anything
      ? { walls: null, reason: 'rebuild-declined' }
      : { walls: stripGeneratedBacksplash(walls), reason: null };
  }
  if (change.off !== false) return { walls, reason: null };
  const { walls: drawn, reason } = planDefaultBacksplash(next, opts);
  return drawn ? { walls: drawn, reason: null } : { walls: null, reason };
}

/** A shape's along-wall and vertical extent, as percentages of the wall. */
function rectOnWall(o, wall) {
  const ext = projectExtentOntoWall([o], wall);
  if (!ext) return null;
  const h = wallHeightOf(wall);
  const base = wallBottomOf(wall);
  const y0 = ((bottomOf(o) - base) / h) * 100;
  const y1 = ((topOf(o) - base) / h) * 100;
  return { x: ext.minP, width: ext.maxP - ext.minP, y: y0, height: y1 - y0 };
}

const SKETCH_ROLES = [
  ['counter', isCountertop],
  ['tall', (o) => isLowerCab(o) && standsThroughBand(o)],
  ['base', isLowerCab],
  ['upper', isUpperCab],
  ['range', isStove],
  ['hood', isHood],
  ['microwave', isMicrowave],
];

/**
 * The wall as the card draws it: its size, its bands, its windows and what
 * stands against it — every rect in percent of the wall, x along it and y up
 * from its bottom, in the frame the bands are built in.
 */
export function backsplashSketch(room, wallId, structureOpenings) {
  const wall = (room?.walls || []).find((w) => w.id === wallId);
  if (!wall) return null;
  const objById = new Map((room.objects || []).map((o) => [o.id, o]));
  const objects = [];
  for (const o of room.objects || []) {
    if (!wallIdsFor(o, objById).includes(wallId)) continue;
    const role = SKETCH_ROLES.find(([, is]) => is(o))?.[0];
    if (!role) continue;
    const rect = rectOnWall(o, wall);
    if (rect) objects.push({ role, ...rect });
  }
  const joined = joinRoomOpenings(room, structureOpenings);
  const windows = (joined.windows || [])
    .filter((w) => w?.parentWallIds?.[0] === wallId && w.center && w.dimensions)
    .map((w) => rectOnWall(w, wall))
    .filter(Boolean);
  return {
    lengthM: wall.dimensions?.x ?? 0,
    heightM: wallHeightOf(wall),
    bands: generatedBandsOn(wall).map((b) => ({
      kind: b.kind,
      x: b.x,
      y: b.y,
      width: b.width,
      height: b.height,
    })),
    windows,
    objects,
  };
}

/**
 * `walls` with each wall's backsplash — its setting and its generated bands —
 * put back as it was in `before`, and everything else on the wall left as it
 * is now: the card's Undo must not take back a paint picked since.
 */
export function restoreBacksplash(walls, before) {
  const was = new Map((before || []).map((w) => [w.id, w]));
  const restoredWalls = (walls || []).map((w) => {
    const old = was.get(w.id);
    if (!old) return w;
    const kept = (w.texture?.regions || []).filter(
      (r) => !isGeneratedKitchenBacksplash(r)
    );
    const restored = withSpec(w, old.backsplashSpec);
    return {
      ...restored,
      texture: {
        ...(w.texture || {}),
        regions: [...kept, ...generatedBandsOn(old)],
      },
    };
  });
  // A tile PICKED since stays picked: re-tile each kind the kitchen now
  // wears a chosen tile of — the undo is of the backsplash's shape.
  const now = kitchenTiles(walls);
  let out = restoredWalls;
  for (const kind of ['counter', 'stove']) {
    const tile = now[kind];
    if (!tile || tile.unchosen) continue;
    out = retextureBacksplashWalls(out, tile.textureId, kind)?.walls ?? out;
  }
  return out;
}
